import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import moment from 'moment';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import PageTitle from '../../components/PageTitle';
import MyLoading from '../../components/MyLoading';
import Api from '../../Api';
import { GA_APP_SECTIONS_CHART_ORDER, getGaAppSectionLabel } from '../../helpers/gaAppSections';
import useComponentVisible from '../../helpers/hooks/useComponentVisible';
import './css/index.scss';

const SECTION_LINE_COLORS = ['#3545DE', '#F44336', '#2E7D32', '#ED6C02', '#9C27B0', '#00695C', '#7B1FA2'];

const DEFAULT_TIME_SECTION_SELECTION = ['organizations', 'events', 'articles'];

const Y_AXIS_METRIC = {
  minutes: 'minutes',
  hours: 'hours',
  sessions: 'sessions',
};

const DATE_PRESET = {
  month: 'month',
  week: 'week',
  yesterday: 'yesterday',
  today: 'today',
  custom: 'custom',
};

function computeRangeForPreset(preset) {
  const now = moment();
  switch (preset) {
    case DATE_PRESET.month:
      return { from: now.clone().subtract(1, 'month'), to: now.clone() };
    case DATE_PRESET.week:
      return {
        from: now.clone().subtract(7, 'days').startOf('day'),
        to: now.clone(),
      };
    case DATE_PRESET.yesterday: {
      const y = now.clone().subtract(1, 'day');
      return {
        from: y.clone().startOf('day'),
        to: y.clone().endOf('day'),
      };
    }
    case DATE_PRESET.today:
      return {
        from: now.clone().startOf('day'),
        to: now.clone(),
      };
    default:
      return { from: now.clone().subtract(1, 'month'), to: now.clone() };
  }
}

function presetToLocalStrings(preset) {
  const { from, to } = computeRangeForPreset(preset);
  return {
    fromLocal: from.format('YYYY-MM-DDTHH:mm'),
    toLocal: to.format('YYYY-MM-DDTHH:mm'),
  };
}

function validateRange(fromMs, toMs, groupBy, bucket) {
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) {
    return 'Укажите корректные даты';
  }
  if (toMs < fromMs) {
    return 'Конец интервала не может быть раньше начала';
  }
  const spanDays = (toMs - fromMs) / 86400000;
  if (spanDays > 366) {
    return 'Интервал не может быть длиннее 366 суток';
  }
  if (groupBy === 'time' && bucket === 'hour' && spanDays > 120) {
    return 'Для шага «час» интервал не длиннее 120 суток';
  }
  return null;
}

function formatBucketLabel(iso, bucket) {
  const m = moment(iso);
  if (bucket === 'day') return m.format('DD.MM.YYYY');
  if (bucket === 'hour') return m.format('DD.MM HH:00');
  return m.format('DD.MM.YYYY HH:mm');
}

function formatYAxisValue(value, yAxisMetric) {
  if (value == null || Number.isNaN(value)) return '—';
  if (yAxisMetric === Y_AXIS_METRIC.sessions) {
    return String(Math.round(Number(value)));
  }
  if (yAxisMetric === Y_AXIS_METRIC.hours) {
    return `${Number(value).toFixed(2)} ч`;
  }
  return `${Number(value).toFixed(1)} мин`;
}

function yAxisLabel(yAxisMetric) {
  if (yAxisMetric === Y_AXIS_METRIC.sessions) return 'Сессии';
  if (yAxisMetric === Y_AXIS_METRIC.hours) return 'Время (часы)';
  return 'Время (минуты)';
}

function formatPeriodDateTime(localValue) {
  return moment(localValue).format('DD.MM.YYYY HH:mm');
}

async function fetchScreenTimeStatsBody(params) {
  try {
    const { data } = await Api.getScreenTimeStats(params);
    return data;
  } catch (e) {
    console.warn(e?.response?.data, 'screen-time stats');
    return e?.response?.data ?? null;
  }
}

function collectSectionColumnsFromRows(rows) {
  const set = new Set();
  rows.forEach((row) => {
    (row.sections || []).forEach((s) => {
      if (s?.section) set.add(s.section);
    });
  });
  const keys = [...set];
  const rank = new Map(GA_APP_SECTIONS_CHART_ORDER.map((k, i) => [k, i]));
  return keys.sort((a, b) => {
    const ra = rank.has(a) ? rank.get(a) : 9999;
    const rb = rank.has(b) ? rank.get(b) : 9999;
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

function defaultSelectedSections(available) {
  const picked = DEFAULT_TIME_SECTION_SELECTION.filter((id) => available.includes(id));
  if (picked.length) return picked;
  return available.slice(0, Math.min(3, available.length));
}

/**
 * Ответ group_by=time: section_columns + rows[].sections[].
 */
function timeGroupResponseToRowPoints(apiResponse, bucket) {
  if (!apiResponse || apiResponse.status !== 'ok' || !apiResponse.rows?.length) {
    return { timeRowPoints: [], sectionKeys: [] };
  }

  const { rows } = apiResponse;
  const sectionColumns = apiResponse.section_columns?.length
    ? [...apiResponse.section_columns]
    : collectSectionColumnsFromRows(rows);

  const timeRowPoints = rows.map((row) => {
    const bySection = {};
    (row.sections || []).forEach((s) => {
      if (s?.section != null) {
        bySection[s.section] = {
          minutes: s.total_duration_ms / 60000,
          sessions: Number(s.sessions_count) || 0,
        };
      }
    });

    const point = {
      sortKey: new Date(row.bucket_start).getTime(),
      name: formatBucketLabel(row.bucket_start, bucket),
      _sections: {},
    };

    sectionColumns.forEach((col) => {
      point._sections[col] = bySection[col] !== undefined
        ? bySection[col]
        : { minutes: undefined, sessions: undefined };
    });

    return point;
  }).sort((a, b) => a.sortKey - b.sortKey);

  return { timeRowPoints, sectionKeys: sectionColumns };
}

function buildFlatTimeChartData(points, selectedIds, yAxisMetric) {
  return points.map((row) => {
    const out = { name: row.name, sortKey: row.sortKey };
    selectedIds.forEach((id) => {
      const cell = row._sections[id];
      if (!cell) {
        out[id] = undefined;
        return;
      }
      if (yAxisMetric === Y_AXIS_METRIC.sessions) {
        out[id] = cell.sessions !== undefined ? cell.sessions : undefined;
      } else if (yAxisMetric === Y_AXIS_METRIC.hours) {
        out[id] = cell.minutes != null ? cell.minutes / 60 : undefined;
      } else {
        out[id] = cell.minutes !== undefined ? cell.minutes : undefined;
      }
    });
    return out;
  });
}

function rowsToSectionChart(apiResponse) {
  if (!apiResponse || apiResponse.status !== 'ok' || !apiResponse.rows?.length) {
    return [];
  }
  return apiResponse.rows.map((r) => ({
    name: getGaAppSectionLabel(r.section),
    duration: r.total_duration_ms / 60000,
    sessions: Number(r.sessions_count) || 0,
  }));
}

function countryLabel(row) {
  if (row.country_name) return row.country_name;
  if (row.country == null || row.country === '') return 'Неизвестно';
  return String(row.country);
}

/**
 * group_by=country: топ стран + разбивка по разделам (stacked bar).
 */
function countryGroupResponseToChart(apiResponse) {
  if (!apiResponse || apiResponse.status !== 'ok' || !apiResponse.rows?.length) {
    return { countryRows: [], sectionKeys: [] };
  }

  const { rows } = apiResponse;
  const sectionColumns = apiResponse.section_columns?.length
    ? [...apiResponse.section_columns]
    : collectSectionColumnsFromRows(rows);

  const countryRows = rows.map((row) => {
    const bySection = {};
    (row.sections || []).forEach((s) => {
      if (s?.section != null) {
        bySection[s.section] = {
          minutes: s.total_duration_ms / 60000,
          sessions: Number(s.sessions_count) || 0,
        };
      }
    });

    const point = {
      name: countryLabel(row),
      country: row.country,
      totalMinutes: row.total_duration_ms / 60000,
      totalSessions: Number(row.sessions_count) || 0,
      _sections: {},
    };

    sectionColumns.forEach((col) => {
      point._sections[col] = bySection[col] !== undefined
        ? bySection[col]
        : { minutes: 0, sessions: 0 };
    });

    return point;
  });

  return { countryRows, sectionKeys: sectionColumns };
}

function buildStackedCountryChartData(countryRows, sectionKeys, yAxisMetric) {
  return countryRows.map((row) => {
    const out = {
      name: row.name,
      country: row.country,
    };
    sectionKeys.forEach((id) => {
      const cell = row._sections[id];
      if (!cell) {
        out[id] = 0;
        return;
      }
      if (yAxisMetric === Y_AXIS_METRIC.sessions) {
        out[id] = cell.sessions || 0;
      } else if (yAxisMetric === Y_AXIS_METRIC.hours) {
        out[id] = (cell.minutes || 0) / 60;
      } else {
        out[id] = cell.minutes || 0;
      }
    });
    return out;
  });
}

function MultiSeriesTooltip({ active, label, payload, yAxisMetric }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="screen_time_statistics_tooltip">
      <p>{label}</p>
      {payload.map((item) => (
        <p key={item.dataKey}>
          {item.name}
          :
          {' '}
          {formatYAxisValue(item.value, yAxisMetric)}
        </p>
      ))}
    </div>
  );
}

function sectionLineColor(sectionId, allColumns) {
  const i = allColumns.indexOf(sectionId);
  const idx = i >= 0 ? i : 0;
  return SECTION_LINE_COLORS[idx % SECTION_LINE_COLORS.length];
}

function pluralRuSections(count) {
  const n = Number(count);
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} раздел`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} раздела`;
  return `${n} разделов`;
}

function ScreenTimeStatistics() {
  const {
    ref: sectionDropdownRef,
    isComponentVisible: sectionDropdownOpen,
    setIsComponentVisible: setSectionDropdownOpen,
  } = useComponentVisible(false);

  const [datePreset, setDatePreset] = useState(DATE_PRESET.month);
  const [fromLocal, setFromLocal] = useState(() => presetToLocalStrings(DATE_PRESET.month).fromLocal);
  const [toLocal, setToLocal] = useState(() => presetToLocalStrings(DATE_PRESET.month).toLocal);
  const [groupBy, setGroupBy] = useState('time');
  const [bucket, setBucket] = useState('hour');
  const [yAxisMetric, setYAxisMetric] = useState(Y_AXIS_METRIC.minutes);

  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [meta, setMeta] = useState(null);

  const [timeRowPoints, setTimeRowPoints] = useState([]);
  const [timeSectionColumns, setTimeSectionColumns] = useState([]);
  const [selectedTimeSections, setSelectedTimeSections] = useState([]);

  const [sectionBarRows, setSectionBarRows] = useState([]);
  const [countryRows, setCountryRows] = useState([]);
  const [countrySectionKeys, setCountrySectionKeys] = useState([]);

  const load = useCallback(async () => {
    setFetchError('');
    const fromMs = new Date(fromLocal).getTime();
    const toMs = new Date(toLocal).getTime();
    const rangeError = validateRange(fromMs, toMs, groupBy, bucket);
    if (rangeError) {
      setFetchError(rangeError);
      setMeta(null);
      setTimeRowPoints([]);
      setTimeSectionColumns([]);
      setSelectedTimeSections([]);
      setSectionBarRows([]);
      setCountryRows([]);
      setCountrySectionKeys([]);
      return;
    }

    const baseParams = {
      from: String(fromMs),
      to: String(toMs),
      group_by: groupBy,
    };
    if (groupBy === 'time') {
      baseParams.bucket = bucket;
    }

    setLoading(true);
    try {
      if (groupBy === 'time') {
        const data = await fetchScreenTimeStatsBody(baseParams);
        if (!data || data.status !== 'ok') {
          const msg = data?.message || data?.error || 'Не удалось загрузить статистику';
          setFetchError(msg);
          setMeta(null);
          setTimeRowPoints([]);
          setTimeSectionColumns([]);
          setSelectedTimeSections([]);
          setSectionBarRows([]);
          setCountryRows([]);
          setCountrySectionKeys([]);
          return;
        }

        const { timeRowPoints: points, sectionKeys: keys } = timeGroupResponseToRowPoints(data, bucket);
        setTimeRowPoints(points);
        setTimeSectionColumns(keys);
        setSelectedTimeSections(defaultSelectedSections(keys));
        setSectionBarRows([]);
        setCountryRows([]);
        setCountrySectionKeys([]);
        setMeta({ mode: 'time-sections', bucket });
        setFetchError('');
        return;
      }

      if (groupBy === 'country') {
        const data = await fetchScreenTimeStatsBody(baseParams);
        if (!data || data.status !== 'ok') {
          const msg = data?.message || data?.error || 'Не удалось загрузить статистику';
          setFetchError(msg);
          setMeta(null);
          setTimeRowPoints([]);
          setTimeSectionColumns([]);
          setSelectedTimeSections([]);
          setSectionBarRows([]);
          setCountryRows([]);
          setCountrySectionKeys([]);
          return;
        }

        const { countryRows: rows, sectionKeys: keys } = countryGroupResponseToChart(data);
        setTimeRowPoints([]);
        setTimeSectionColumns([]);
        setSelectedTimeSections([]);
        setSectionBarRows([]);
        setCountryRows(rows);
        setCountrySectionKeys(keys);
        setMeta({ mode: 'country' });
        setFetchError('');
        return;
      }

      const data = await fetchScreenTimeStatsBody(baseParams);
      if (!data || data.status !== 'ok') {
        const msg = data?.message || data?.error || 'Не удалось загрузить статистику';
        setFetchError(msg);
        setMeta(null);
        setTimeRowPoints([]);
        setTimeSectionColumns([]);
        setSelectedTimeSections([]);
        setSectionBarRows([]);
        setCountryRows([]);
        setCountrySectionKeys([]);
        return;
      }

      setTimeRowPoints([]);
      setTimeSectionColumns([]);
      setSelectedTimeSections([]);
      setCountryRows([]);
      setCountrySectionKeys([]);
      setSectionBarRows(rowsToSectionChart(data));
      setMeta({ mode: 'section' });
      setFetchError('');
    } finally {
      setLoading(false);
    }
  }, [fromLocal, toLocal, groupBy, bucket]);

  useEffect(() => {
    load();
  }, [load]);

  const timeChartData = useMemo(() => {
    if (!timeRowPoints.length || !selectedTimeSections.length) return [];
    return buildFlatTimeChartData(timeRowPoints, selectedTimeSections, yAxisMetric);
  }, [timeRowPoints, selectedTimeSections, yAxisMetric]);

  const sectionBarChartData = useMemo(() => {
    if (!sectionBarRows.length) return [];
    return sectionBarRows.map((r) => {
      let plotValue;
      if (yAxisMetric === Y_AXIS_METRIC.sessions) plotValue = r.sessions;
      else if (yAxisMetric === Y_AXIS_METRIC.hours) plotValue = r.duration / 60;
      else plotValue = r.duration;
      return { ...r, plotValue };
    });
  }, [sectionBarRows, yAxisMetric]);

  const countryChartData = useMemo(() => {
    if (!countryRows.length || !countrySectionKeys.length) return [];
    return buildStackedCountryChartData(countryRows, countrySectionKeys, yAxisMetric);
  }, [countryRows, countrySectionKeys, yAxisMetric]);

  const yLabel = yAxisLabel(yAxisMetric);

  const isTimeMultiSection = meta?.mode === 'time-sections' && timeSectionColumns.length > 0;
  const isCountryMode = meta?.mode === 'country' && countryChartData.length > 0;
  const hasAnyChart = timeChartData.length > 0 || sectionBarChartData.length > 0 || countryChartData.length > 0;

  const onDatePresetChange = (e) => {
    const next = e.target.value;
    setDatePreset(next);
    if (next !== DATE_PRESET.custom) {
      const { fromLocal: f, toLocal: t } = presetToLocalStrings(next);
      setFromLocal(f);
      setToLocal(t);
    }
  };

  const toggleTimeSection = (id) => {
    setSelectedTimeSections((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const sectionDropdownSummary = useMemo(() => {
    if (!selectedTimeSections.length) return 'Не выбрано';
    if (selectedTimeSections.length === timeSectionColumns.length) return 'Все разделы';
    const n = selectedTimeSections.length;
    if (n > 3) return `Выбрано ${pluralRuSections(n)}`;
    return selectedTimeSections.map((id) => getGaAppSectionLabel(id)).join(', ');
  }, [selectedTimeSections, timeSectionColumns.length]);

  const onSectionDropdownToggle = (e) => {
    e.stopPropagation();
    setSectionDropdownOpen((v) => !v);
  };

  return (
    <div className="screen_time_statistics">
      <PageTitle
        text="Статистика экранов"
        RightComponent={<MyLoading height={30} width={30} strokeWidth={4} visible={loading} />}
      />

      <div className="screen_time_statistics_filters">
        <div className="screen_time_statistics_field">
          <label htmlFor="st_period">Период</label>
          <select
            id="st_period"
            className="screen_time_statistics_select"
            value={datePreset}
            onChange={onDatePresetChange}
          >
            <option value={DATE_PRESET.month}>За месяц</option>
            <option value={DATE_PRESET.week}>За неделю</option>
            <option value={DATE_PRESET.yesterday}>За вчера</option>
            <option value={DATE_PRESET.today}>За сегодня</option>
            <option value={DATE_PRESET.custom}>Другое</option>
          </select>
        </div>
        {datePreset === DATE_PRESET.custom ? (
          <>
            <div className="screen_time_statistics_field">
              <label htmlFor="st_from">С</label>
              <input
                id="st_from"
                type="datetime-local"
                value={fromLocal}
                onChange={(e) => setFromLocal(e.target.value)}
              />
            </div>
            <div className="screen_time_statistics_field">
              <label htmlFor="st_to">По</label>
              <input
                id="st_to"
                type="datetime-local"
                value={toLocal}
                onChange={(e) => setToLocal(e.target.value)}
              />
            </div>
          </>
        ) : null}
        <div className="screen_time_statistics_field">
          <label htmlFor="st_group">Режим</label>
          <select
            id="st_group"
            className="screen_time_statistics_select"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="time">По времени</option>
            <option value="section">По разделам</option>
            <option value="country">По странам</option>
          </select>
        </div>
        {groupBy === 'time' && (
          <div className="screen_time_statistics_field">
            <label htmlFor="st_bucket">Шаг по дате</label>
            <select
              id="st_bucket"
              className="screen_time_statistics_select"
              value={bucket}
              onChange={(e) => setBucket(e.target.value)}
            >
              <option value="hour">Час</option>
              <option value="day">День</option>
            </select>
          </div>
        )}
        {groupBy === 'time' && timeSectionColumns.length > 0 ? (
          <div className="screen_time_statistics_field screen_time_statistics_field_dropdown" ref={sectionDropdownRef}>
            <span className="screen_time_statistics_dropdown_label">Разделы</span>
            <button
              type="button"
              className="screen_time_statistics_dropdown_trigger screen_time_statistics_select_like"
              onClick={onSectionDropdownToggle}
            >
              {sectionDropdownSummary}
            </button>
            {sectionDropdownOpen ? (
              <div
                className="screen_time_statistics_dropdown_panel"
                role="listbox"
                onClick={(e) => e.stopPropagation()}
              >
                {timeSectionColumns.map((id) => (
                  <label key={id} className="screen_time_statistics_dropdown_row">
                    <input
                      type="checkbox"
                      checked={selectedTimeSections.includes(id)}
                      onChange={() => toggleTimeSection(id)}
                    />
                    <span>{getGaAppSectionLabel(id)}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="screen_time_statistics_field">
          <label htmlFor="st_y">Ось Y</label>
          <select
            id="st_y"
            className="screen_time_statistics_select"
            value={yAxisMetric}
            onChange={(e) => setYAxisMetric(e.target.value)}
          >
            <option value={Y_AXIS_METRIC.minutes}>Минуты</option>
            <option value={Y_AXIS_METRIC.hours}>Часы</option>
            <option value={Y_AXIS_METRIC.sessions}>Сессии</option>
          </select>
        </div>
      </div>

      {fetchError ? (
        <div className="screen_time_statistics_error" role="alert">
          {fetchError}
        </div>
      ) : null}

      <div className="screen_time_statistics_chart_wrap">
        <div className="screen_time_statistics_chart_period">
          <span>
            С
            {' '}
            {formatPeriodDateTime(fromLocal)}
          </span>
          <span className="screen_time_statistics_chart_period_sep">—</span>
          <span>
            По
            {' '}
            {formatPeriodDateTime(toLocal)}
          </span>
        </div>

        {loading && !hasAnyChart ? (
          <div className="screen_time_statistics_loading">
            <MyLoading strokeWidth={4} width={40} height={40} />
          </div>
        ) : null}

        {!loading && meta?.mode === 'time-sections' && timeRowPoints.length > 0 && !selectedTimeSections.length ? (
          <p className="screen_time_statistics_empty">Выберите хотя бы один раздел</p>
        ) : null}

        {!loading && meta && !hasAnyChart
          && !(meta?.mode === 'time-sections' && timeRowPoints.length > 0 && !selectedTimeSections.length) ? (
            <p className="screen_time_statistics_empty">Нет данных</p>
          ) : null}

        {timeChartData.length > 0 && isTimeMultiSection ? (
          <ResponsiveContainer width="100%" height={420}>
            <LineChart data={timeChartData} margin={{ top: 8, right: 24, left: 8, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e8eb" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7F8A99' }} interval={0} angle={-18} textAnchor="end" height={56} />
              <YAxis
                tick={{ fontSize: 11, fill: '#3545DE' }}
                label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: '#3545DE', fontSize: 12 }}
              />
              <Tooltip content={<MultiSeriesTooltip yAxisMetric={yAxisMetric} />} />
              <Legend />
              {selectedTimeSections.map((key) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={getGaAppSectionLabel(key)}
                  stroke={sectionLineColor(key, timeSectionColumns)}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : null}

        {sectionBarChartData.length > 0 && meta?.mode === 'section' ? (
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={sectionBarChartData} margin={{ top: 8, right: 16, left: 8, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e8eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#7F8A99' }}
                interval={0}
                angle={-16}
                textAnchor="end"
                height={56}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#3545DE' }}
                label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: '#3545DE', fontSize: 12 }}
              />
              <Tooltip
                content={({ active, label, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="screen_time_statistics_tooltip">
                      <p>{label}</p>
                      <p>
                        {yLabel}
                        :
                        {' '}
                        {formatYAxisValue(payload[0]?.value, yAxisMetric)}
                      </p>
                    </div>
                  );
                }}
              />
              <Legend />
              <Bar dataKey="plotValue" name={yLabel} fill="#3545DE" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        ) : null}

        {isCountryMode ? (
          <ResponsiveContainer width="100%" height={440}>
            <BarChart data={countryChartData} margin={{ top: 8, right: 16, left: 8, bottom: 56 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e8eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#7F8A99' }}
                interval={0}
                angle={-28}
                textAnchor="end"
                height={72}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#3545DE' }}
                label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: '#3545DE', fontSize: 12 }}
              />
              <Tooltip content={<MultiSeriesTooltip yAxisMetric={yAxisMetric} />} />
              <Legend />
              {countrySectionKeys.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={getGaAppSectionLabel(key)}
                  stackId="country"
                  fill={SECTION_LINE_COLORS[i % SECTION_LINE_COLORS.length]}
                  maxBarSize={48}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : null}
      </div>
    </div>
  );
}

export default ScreenTimeStatistics;
